import { CopyIcon, EyeIcon, EyeOffIcon, RefreshCwIcon } from 'lucide-react'
import { useState, type SubmitEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { setUserPassword, type ManagedUser } from '@/api/users'
import { useApiErrorMessage } from '@/api/errors'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { userLabel } from '@/lib/userDisplay'
import {
  PASSWORD_MIN_LENGTH,
  generatePassword,
  meetsPasswordPolicy,
} from '@/components/users/validation'

/**
 * @param activating the same request, worded as what it is for an invited account: the last
 *   resort when the invitation never arrives. A password makes the account active — temporary or
 *   not — so the administrator hands it over in person instead of waiting for the mail.
 */
export function SetPasswordDialog({
  user,
  activating = false,
  onOpenChange,
  onDone,
}: {
  user: ManagedUser | null
  activating?: boolean
  onOpenChange: (open: boolean) => void
  onDone: () => void
}) {
  return (
    <Dialog open={user !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {/* Mounted fresh per user and thrown away on close, so a password is never carried over
            to the next person and never left sitting in state behind a closed dialog. */}
        {user && (
          <PasswordForm
            key={user.id}
            user={user}
            activating={activating}
            onOpenChange={onOpenChange}
            onDone={onDone}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

function PasswordForm({
  user,
  activating,
  onOpenChange,
  onDone,
}: {
  user: ManagedUser
  activating: boolean
  onOpenChange: (open: boolean) => void
  onDone: () => void
}) {
  const { t } = useTranslation()
  const errorMessage = useApiErrorMessage()
  const name = userLabel(user, t('users.noName'))

  const [password, setPassword] = useState('')
  const [visible, setVisible] = useState(false)
  // The default an administrator should almost always keep: a password they know is one that
  // could be reused, so the user has to replace it at the next sign-in.
  const [temporary, setTemporary] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(password)
      toast.success(t('users.password.copied'))
    } catch {
      // Denied permission, or an insecure origin. Nothing to retry — the field is selectable.
      toast.error(t('users.password.copyFailed'))
    }
  }

  async function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    if (submitting) {
      return
    }
    // The realm's policy is the real check, but failing it should not cost a round trip.
    if (!meetsPasswordPolicy(password)) {
      setError(
        password.length < PASSWORD_MIN_LENGTH
          ? t('validation.passwordTooShort', { count: PASSWORD_MIN_LENGTH })
          : t('users.password.policy'),
      )
      return
    }
    setError(null)
    setSubmitting(true)
    setFailure(null)
    try {
      await setUserPassword(user.id, { password, temporary })
      toast.success(activating ? t('users.activate.success', { name }) : t('users.password.success'))
      onDone()
      onOpenChange(false)
    } catch (requestError) {
      setFailure(
        errorMessage(requestError, activating ? t('users.activate.failed') : t('users.password.failed')),
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      <DialogHeader>
        <DialogTitle>{activating ? t('users.activate.title') : t('users.password.title')}</DialogTitle>
        <DialogDescription>
          {activating
            ? t('users.activate.description', { name })
            : t('users.password.description', { name })}
        </DialogDescription>
      </DialogHeader>

      <div className="grid gap-4 py-6">
        {failure && (
          <Alert variant="destructive">
            <AlertDescription>{failure}</AlertDescription>
          </Alert>
        )}

        <div className="grid gap-2">
          <Label htmlFor="set-password">{t('users.password.label')}</Label>
          <div className="flex gap-2">
            <Input
              id="set-password"
              // Not `type="password"` unconditionally: an administrator setting somebody else's
              // password has to be able to read back what they are about to hand over.
              type={visible ? 'text' : 'password'}
              // Never offer to remember or fill this — it is not the administrator's password.
              autoComplete="off"
              spellCheck={false}
              className="font-mono"
              value={password}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? 'set-password-error' : 'set-password-policy'}
              onChange={(event) => setPassword(event.target.value)}
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={visible ? t('users.password.hide') : t('users.password.show')}
              onClick={() => setVisible(!visible)}
            >
              {visible ? <EyeOffIcon /> : <EyeIcon />}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label={t('users.password.copy')}
              disabled={!password}
              onClick={() => void copy()}
            >
              <CopyIcon />
            </Button>
          </div>

          <div className="flex items-center justify-between gap-2">
            <p
              id={error ? 'set-password-error' : 'set-password-policy'}
              className={error ? 'text-destructive text-xs' : 'text-muted-foreground text-xs'}
            >
              {error ?? t('users.password.policy')}
            </p>
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={() => {
                setPassword(generatePassword())
                // Generating one the administrator cannot read helps nobody.
                setVisible(true)
                setError(null)
              }}
            >
              <RefreshCwIcon />
              {t('users.password.generate')}
            </Button>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Checkbox
            id="set-password-temporary"
            checked={temporary}
            onCheckedChange={(checked) => setTemporary(checked === true)}
          />
          <div className="grid gap-1">
            <Label htmlFor="set-password-temporary">{t('users.password.temporary')}</Label>
            <p className="text-muted-foreground text-xs">{t('users.password.temporaryHint')}</p>
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={submitting}>
          {activating ? t('users.activate.submit') : t('users.password.submit')}
        </Button>
      </DialogFooter>
    </form>
  )
}
